import { createErrorHandler } from "@/app-utils/errors.ts";
import type { Contig } from "@/graphql.ts";
import { LOAD_CONTIG_FAILURE, LOAD_CONTIG_REQUEST, LOAD_CONTIG_SUCCESS } from "@/redux/action-types.ts";
import type WebApiClient from "../../api/WebApiClient.ts";
import type { IAppState } from "../reducer.ts";
import { createReducer } from "../redux-utils.ts";

/**
 * State definition
 */
export interface IContigState {
  contig?: Contig;
  isLoading: boolean;
}

const initialState: IContigState = {
  isLoading: false,
  contig: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_CONTIG_REQUEST]: (state: IContigState, _payload: Contig): IContigState => {
    return { ...state, isLoading: true };
  },
  [LOAD_CONTIG_SUCCESS]: (state: IContigState, payload: Contig): IContigState => {
    return { ...state, isLoading: false, contig: payload };
  },
  [LOAD_CONTIG_FAILURE]: (state: IContigState, _payload): IContigState => {
    return { ...state, isLoading: false, contig: null };
  },
});

/**
 * Action creators
 */
let lastFetchedContigId = -1;

export function shouldLoadContig(contigId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<Contig> => {
    const state: IAppState = getState();
    lastFetchedContigId = contigId;

    // fetch the contig only if no loading is currently in progress and contig is either absent or doesn't match the contig ID
    if (!state.contig.isLoading && (!state.contig.contig || state.contig.contig.id !== contigId)) {
      dispatch({ type: LOAD_CONTIG_REQUEST });

      return webApiClient
        .fetchContig(contigId)
        .then((response: Contig) => {
          if (lastFetchedContigId === response.id) {
            dispatch({
              type: LOAD_CONTIG_SUCCESS,
              payload: response,
            });

            return response;
          }

          return state.contig.contig;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_CONTIG_FAILURE,
            alertTitle: "Fetching of Contig Failed",
          }),
        );
    }
  };
}
